import 'core-js/stable';
import '@cloudscape-design/global-styles/index.css';

import React from 'react';
import { createRoot } from 'react-dom/client';

import App from './components/App';
import { getMidwayJwtToken } from './auth/MidwayJwtToken';
import { loadRuntimeConfig } from './config/ApiConfig';

// Cloudscape charts and tables (sticky header + resizable columns) can trigger a benign
// "ResizeObserver loop completed with undelivered notifications" error when a component
// resizes inside a container that resizes in the same frame (e.g. the stacked divergence
// charts, or toggling between Chart and Table). The browser self-recovers, but the error is
// surfaced as a fatal red overlay in dev. Deferring the observer callback to the next animation
// frame breaks the synchronous resize loop, so the error is never emitted at all.
if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends NativeResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

async function bootstrap(): Promise<void> {
  // Load per-stage runtime config before anything makes API calls.
  await loadRuntimeConfig();

  try {
    await getMidwayJwtToken();
  } catch (error) {
    // Expected to fail on localhost; UI still renders, local backend doesn't check auth.
    if (!window.location.hostname.includes('localhost')) {
      console.error('[Midway] Failed to obtain JWT token:', error);
    }
  }

  const container = document.getElementById('app');
  if (!container) {
    throw new Error('Root element #app not found');
  }

  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

void bootstrap();
